import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, Card, CardContent, Button, 
  Divider, Chip, IconButton, CircularProgress, Alert, Tooltip, Badge,
  Dialog, DialogContent, DialogTitle, Checkbox, FormControlLabel
} from '@mui/material';
import {
  EventNote as CalendarIcon,
  Event as EventIcon,
  EventAvailable as MeetingIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../api';
import SalesOutreachShell from './SalesOutreachShell';
import OutreachStats from './OutreachStats';
import { useAuth } from '../context/AuthContext';
import logger from './../utils/logger.js';

const EventsDashboard = ({ daysAhead = 7 }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todayEvents, setTodayEvents] = useState({ 
    today_meetings: [], 
    today_followups: [], 
    upcoming_meetings: [],
    upcoming_followups: [],
    past_meetings: [],
    past_followups: []
  });
  const [viewMode, setViewMode] = useState('today'); // 'today', 'upcoming', or 'past'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openOutreachDialog, setOpenOutreachDialog] = useState(false);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);
  
  // Add a state variable to track when outreach stats should refresh
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  
  // Function to refresh outreach statistics
  const refreshStats = () => {
    logger.log('EventsDashboard: Refreshing outreach stats, incrementing statsRefreshKey');
    setStatsRefreshKey(prevKey => prevKey + 1);
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead, includeCompleted]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getTodayEvents(daysAhead, includeCompleted);
      if (response.success) {
        setTodayEvents(response);
      } else {
        setError(response.message || 'Failed to load events');
      }
    } catch (err) {
      logger.error('Error loading events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Format date nicely with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, h:mm a');
    } catch (err) {
      logger.error('Error formatting date:', err);
      return dateString;
    }
  };
  
  // Format date nicely with just date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'EEEE, dd MMM');
    } catch (err) {
      logger.error('Error formatting date:', err);
      return dateString;
    }
  };

  // Get icon based on outreach method
  const getOutreachMethodIcon = (method) => {
    switch (method) {
      case 'phone':
        return <PhoneIcon fontSize="small" />;
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'linkedin':
        return <LinkedInIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Get color based on type of event
  const getEventColor = (isFollowup) => {
    return isFollowup ? 'info.main' : 'success.main';
  };

  // Handle click on event card
  const handleEventClick = async (event) => {
    try {
      // Set selected event to current event initially so we have something to show
      setSelectedEvent(event);
      
      // Don't block UI while fetching data
      setOpenOutreachDialog(true);
      
      // Then try to fetch complete record data for this event
      if (event.record_id) {
        logger.log("Fetching complete record data for event:", event);
        
        try {
          const response = await api.getRecord(event.record_id);
          
          if (response && response.success && response.data) {
            logger.log("Fetched complete record data:", response.data);
            
            // Get the website URL - try all possible field names
            const website = response.data.URL || 
                           response.data.url || 
                           response.data.Website || 
                           response.data.website || 
                           response.data.Domain || 
                           response.data.domain || '';
                           
            logger.log("Extracted website URL:", website);
            
            // Combine the event data with the complete record data
            const completeEvent = {
              ...event,
              // Make sure URL fields are present in various formats for maximum compatibility
              URL: website,
              url: website,
              Website: website,
              website: website,
              Domain: website,
              domain: website,
              // Add all additional fields from the data record
              ...response.data
            };
            
            logger.log("Combined event with record data:", completeEvent);
            
            // Update the selected event with the complete data
            setSelectedEvent(completeEvent);
          } else {
            logger.warn("API response didn't contain record data:", response);
            // We already have the basic event data set, so we can continue
          }
        } catch (fetchError) {
          logger.error("Error fetching record data:", fetchError);
          // We already have the basic event data set, so we can continue
        }
      } else {
        logger.warn("Event has no record_id, using event data as is:", event);
      }
    } catch (err) {
      logger.error("Error in handleEventClick:", err);
      // Ensure dialog is open even if there's an error
      setOpenOutreachDialog(true);
    }
  };

  // Close outreach dialog - refreshing stats when dialog closes
  const handleCloseOutreachDialog = () => {
    setOpenOutreachDialog(false);
    
    // Refresh stats when dialog closes
    refreshStats();
    
    // Reload events after dialog closes to refresh any changes
    setTimeout(() => {
      loadEvents();
    }, 500);
  };

  // Handle toggling completion status - refresh stats after toggling
  const handleToggleCompletion = async (eventId, isCompleted) => {
    try {
      const response = await api.toggleOutreachCompletion(eventId, isCompleted);
      
      if (response.success) {
        // Update locally to avoid full reload
        const updatedEvents = { ...todayEvents };
        
        // Create utility function to update completion status in arrays
        const updateCompletionStatus = (eventsList) => {
          return eventsList.map(e => {
            if (e.id === eventId) {
              return { ...e, completed: isCompleted, completed_date: isCompleted ? new Date().toISOString() : null };
            }
            return e;
          });
        };
        
        // Update all relevant event arrays
        updatedEvents.today_followups = updateCompletionStatus(updatedEvents.today_followups);
        updatedEvents.upcoming_followups = updateCompletionStatus(updatedEvents.upcoming_followups);
        updatedEvents.past_followups = updateCompletionStatus(updatedEvents.past_followups);
        
        setTodayEvents(updatedEvents);
        
        // Refresh stats after toggling completion
        refreshStats();
      } else {
        logger.error('Failed to update completion status:', response.message);
      }
    } catch (err) {
      logger.error('Error toggling completion status:', err);
    }
  };

  // Event card component
  const EventCard = ({ event, isFollowup }) => {
    const eventDate = isFollowup ? event.followup_date : event.meeting_date;
    const isExpanded = expandedEventId === event.id;
    
    // Handle expand toggle
    const handleExpandClick = (e) => {
      e.stopPropagation();
      setExpandedEventId(isExpanded ? null : event.id);
    };
    
    return (
      <Card 
        sx={{ 
          mb: 1, 
          borderLeft: 4, 
          borderColor: getEventColor(isFollowup),
          opacity: event.completed ? 0.7 : 1, // Fade completed items
          '&:hover': {
            boxShadow: 3,
            cursor: 'pointer'
          }
        }}
        onClick={() => handleEventClick(event)}
      >
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', mr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isFollowup ? <EventIcon fontSize="small" color="info" sx={{ mr: 1, flexShrink: 0 }} /> : <MeetingIcon fontSize="small" color="success" sx={{ mr: 1, flexShrink: 0 }} />}
                <Typography variant="subtitle2" fontWeight="bold" component="div">
                  {event.company_name || 'Company Name Not Available'}
                </Typography>
              </Box>
              {event.contact_name && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                  {event.contact_name}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {isFollowup && (
                <Tooltip title={event.completed ? "Mark as not completed" : "Mark as completed"}>
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent the card click event from triggering
                      handleToggleCompletion(event.id, !event.completed);
                    }}
                    sx={{ mr: 0.5 }}
                  >
                    {event.completed ? 
                      <CheckCircleIcon fontSize="small" color="success" /> : 
                      <UncheckedIcon fontSize="small" color="action" />
                    }
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="Show details">
                <IconButton size="small" onClick={handleExpandClick} sx={{ mr: 0.5 }}>
                  {isExpanded ? 
                    <KeyboardArrowUpIcon fontSize="small" /> : 
                    <KeyboardArrowDownIcon fontSize="small" />
                  }
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Open full details">
                <OpenInNewIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
          </Box>
          
          {isExpanded && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" component="div">
                  {formatDateTime(eventDate)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" component="div">
                  {event.contact_name}
                </Typography>
              </Box>
              
              {event.contact_detail && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  {getOutreachMethodIcon(event.outreach_method)}
                  <Typography variant="body2" sx={{ ml: 1 }}>{event.contact_detail}</Typography>
                </Box>
              )}
              
              {event.company_address && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" component="div">
                    {event.company_address}
                  </Typography>
                </Box>
              )}
              
              {event.notes && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                  <NoteIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {event.notes.length > 100 ? `${event.notes.substring(0, 100)}...` : event.notes}
                  </Typography>
                </Box>
              )}
              
              {event.completed && (
                <Box sx={{ display: 'flex', mt: 1 }}>
                  <Chip 
                    size="small" 
                    label="Completed" 
                    color="success" 
                    variant="outlined"
                    icon={<CheckCircleIcon fontSize="small" />}
                  />
                  {event.completed_date && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                      on {formatDateTime(event.completed_date)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Group upcoming events by date
  const groupEventsByDate = (events, isFollowup) => {
    const grouped = {};
    
    events.forEach(event => {
      const dateString = isFollowup ? event.followup_date : event.meeting_date;
      if (!dateString) return;
      
      const date = format(new Date(dateString), 'yyyy-MM-dd');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    
    return grouped;
  };

  // Component to show upcoming events grouped by date
  const UpcomingEventsView = ({ meetings, followups }) => {
    const groupedMeetings = groupEventsByDate(meetings, false);
    const groupedFollowups = groupEventsByDate(followups, true);
    const [expandedDate, setExpandedDate] = useState(null);
    
    // Combine all dates from both categories
    const allDates = [...new Set([
      ...Object.keys(groupedMeetings), 
      ...Object.keys(groupedFollowups)
    ])].sort();

    // Handle date section expand/collapse
    const handleDateClick = (date) => {
      setExpandedDate(expandedDate === date ? null : date);
    };
    
    return (
      <Box sx={{ mt: 3 }}>
        {allDates.map(date => (
          <Box key={date} sx={{ mb: 2 }}>
            <Paper 
              elevation={1}
              sx={{ 
                mb: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' }
              }}
              onClick={() => handleDateClick(date)}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  px: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    {formatDate(date)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {groupedMeetings[date]?.length > 0 && (
                    <Chip 
                      size="small" 
                      icon={<MeetingIcon fontSize="small" sx={{ color: 'white !important' }} />} 
                      label={`${groupedMeetings[date].length} meetings`}
                      sx={{ mr: 1, bgcolor: 'success.main', color: 'white' }}
                    />
                  )}
                  
                  {groupedFollowups[date]?.length > 0 && (
                    <Chip 
                      size="small" 
                      icon={<EventIcon fontSize="small" sx={{ color: 'white !important' }} />} 
                      label={`${groupedFollowups[date].length} follow-ups`}
                      sx={{ mr: 1, bgcolor: 'info.main', color: 'white' }}
                    />
                  )}
                  
                  <IconButton size="small">
                    {expandedDate === date ? 
                      <KeyboardArrowUpIcon fontSize="small" /> : 
                      <KeyboardArrowDownIcon fontSize="small" />
                    }
                  </IconButton>
                </Box>
              </Box>
            </Paper>
            
            {expandedDate === date && (
              <Box sx={{ ml: 2 }}>
                {groupedMeetings[date]?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="success.main" fontWeight="medium" sx={{ mb: 1, px: 1 }}>
                      <MeetingIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Meetings
                    </Typography>
                    {groupedMeetings[date].map(event => (
                      <EventCard key={`meeting-${event.id}`} event={event} isFollowup={false} />
                    ))}
                  </Box>
                )}
                
                {groupedFollowups[date]?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="info.main" fontWeight="medium" sx={{ mb: 1, px: 1 }}>
                      <EventIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Follow-ups
                    </Typography>
                    {groupedFollowups[date].map(event => (
                      <EventCard key={`followup-${event.id}`} event={event} isFollowup={true} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ))}

        {allDates.length === 0 && (
          <Alert severity="info">No upcoming events scheduled in the next {daysAhead} days.</Alert>
        )}
      </Box>
    );
  };

  // Component to show past events grouped by date
  const PastEventsView = ({ meetings, followups }) => {
    const groupedMeetings = groupEventsByDate(meetings, false);
    const groupedFollowups = groupEventsByDate(followups, true);
    const [expandedDate, setExpandedDate] = useState(null);
    
    // Combine all dates from both categories
    const allDates = [...new Set([
      ...Object.keys(groupedMeetings), 
      ...Object.keys(groupedFollowups)
    ])].sort().reverse(); // Sort in reverse order to show most recent first
    
    // Handle date section expand/collapse
    const handleDateClick = (date) => {
      setExpandedDate(expandedDate === date ? null : date);
    };
    
    return (
      <Box sx={{ mt: 3 }}>
        {followups.length > 0 && !includeCompleted && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => setIncludeCompleted(true)}
              >
                Show Completed
              </Button>
            }
          >
            You have {followups.length} incomplete past follow-ups. These need attention!
          </Alert>
        )}
        
        {allDates.map(date => (
          <Box key={date} sx={{ mb: 2 }}>
            <Paper 
              elevation={1}
              sx={{ 
                mb: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' }
              }}
              onClick={() => handleDateClick(date)}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  px: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    {formatDate(date)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {groupedMeetings[date]?.length > 0 && (
                    <Chip 
                      size="small" 
                      icon={<MeetingIcon fontSize="small" sx={{ color: 'white !important' }} />} 
                      label={`${groupedMeetings[date].length} meetings`}
                      sx={{ mr: 1, bgcolor: 'success.main', color: 'white' }}
                    />
                  )}
                  
                  {groupedFollowups[date]?.length > 0 && (
                    <Chip 
                      size="small" 
                      icon={<EventIcon fontSize="small" sx={{ color: 'white !important' }} />} 
                      label={`${groupedFollowups[date].length} follow-ups`}
                      sx={{ mr: 1, bgcolor: 'info.main', color: 'white' }}
                    />
                  )}
                  
                  <IconButton size="small">
                    {expandedDate === date ? 
                      <KeyboardArrowUpIcon fontSize="small" /> : 
                      <KeyboardArrowDownIcon fontSize="small" />
                    }
                  </IconButton>
                </Box>
              </Box>
            </Paper>
            
            {expandedDate === date && (
              <Box sx={{ ml: 2 }}>
                {groupedMeetings[date]?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="success.main" fontWeight="medium" sx={{ mb: 1, px: 1 }}>
                      <MeetingIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Past Meetings
                    </Typography>
                    {groupedMeetings[date].map(event => (
                      <EventCard key={`meeting-${event.id}`} event={event} isFollowup={false} />
                    ))}
                  </Box>
                )}
                
                {groupedFollowups[date]?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="info.main" fontWeight="medium" sx={{ mb: 1, px: 1 }}>
                      <EventIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Past Follow-ups
                    </Typography>
                    {groupedFollowups[date].map(event => (
                      <EventCard key={`followup-${event.id}`} event={event} isFollowup={true} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ))}

        {allDates.length === 0 && (
          <Alert severity="info">No past events found.</Alert>
        )}
      </Box>
    );
  };

  // Main render
  return (
    <>
      {/* Add OutreachStats at the top with refreshKey prop */}
      <OutreachStats refreshKey={statsRefreshKey} />
      
      {/* Only show Today's Schedule section if user has enabled outreach performance */}
      {user?.show_outreach_performance && (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            {viewMode === 'today' ? "Today's Schedule" : 
             viewMode === 'upcoming' ? "Upcoming Schedule" : 
             "Past Events"}
          </Typography>
          
          <Box>
            <Button 
              size="small" 
              variant={viewMode === 'today' ? "contained" : "outlined"} 
              onClick={() => setViewMode('today')}
              sx={{ mr: 1 }}
            >
              Today
            </Button>
            <Button 
              size="small" 
              variant={viewMode === 'upcoming' ? "contained" : "outlined"} 
              onClick={() => setViewMode('upcoming')}
              sx={{ mr: 1 }}
            >
              Upcoming
            </Button>
            <Button 
              size="small" 
              variant={viewMode === 'past' ? "contained" : "outlined"} 
              onClick={() => setViewMode('past')}
              sx={{ mr: 1, position: 'relative' }}
            >
              Past
              {todayEvents.past_followups.length > 0 && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: -8, 
                    right: -8,
                    bgcolor: '#ff9800',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  {todayEvents.past_followups.length}
                </Box>
              )}
            </Button>
            <Tooltip title="Refresh">
              <IconButton onClick={loadEvents} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeCompleted}
                onChange={(e) => setIncludeCompleted(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={viewMode === 'past' ? 
              "Show completed follow-ups (past)" : 
              `Show completed follow-ups (${viewMode === 'upcoming' ? "upcoming" : "today"})`}
          />
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Event count badges */}
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                icon={<EventIcon sx={{ color: 'white' }} />} 
                label={`${todayEvents.today_followups.length} today's follow-ups`} 
                size="small"
                sx={{ 
                  bgcolor: '#2196f3', // info blue
                  color: 'white',
                  borderRadius: 1,
                  '& .MuiChip-label': { fontWeight: 500 }
                }}
                onClick={() => setViewMode('today')}
              />
              <Chip 
                icon={<MeetingIcon sx={{ color: 'white' }} />} 
                label={`${todayEvents.today_meetings.length} today's meetings`} 
                size="small"
                sx={{ 
                  bgcolor: '#4caf50', // success green
                  color: 'white',
                  borderRadius: 1,
                  '& .MuiChip-label': { fontWeight: 500 }
                }}
                onClick={() => setViewMode('today')}
              />
              <Chip 
                icon={<EventIcon sx={{ color: 'white' }} />} 
                label={`${todayEvents.upcoming_followups.length} upcoming follow-ups`} 
                size="small"
                sx={{ 
                  bgcolor: '#2196f3', // info blue
                  color: 'white',
                  borderRadius: 1,
                  '& .MuiChip-label': { fontWeight: 500 }
                }}
                onClick={() => setViewMode('upcoming')}
              />
              <Chip 
                icon={<MeetingIcon sx={{ color: 'white' }} />} 
                label={`${todayEvents.upcoming_meetings.length} upcoming meetings`} 
                size="small"
                sx={{ 
                  bgcolor: '#4caf50', // success green
                  color: 'white',
                  borderRadius: 1,
                  '& .MuiChip-label': { fontWeight: 500 }
                }}
                onClick={() => setViewMode('upcoming')}
              />
              <Chip 
                icon={<EventIcon sx={{ color: 'white' }} />} 
                label={`${todayEvents.past_followups.length} incomplete past follow-ups`} 
                size="small"
                sx={{ 
                  bgcolor: '#ff9800', // warning orange
                  color: 'white',
                  borderRadius: 1,
                  '& .MuiChip-label': { fontWeight: 500 }
                }}
                onClick={() => setViewMode('past')}
              />
            </Box>
            
            {viewMode === 'today' ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={1}
                    sx={{ 
                      mb: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.50' }
                      }}
                      onClick={() => setExpandedEventId(expandedEventId === 'today_meetings' ? null : 'today_meetings')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MeetingIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium" color="success.main">
                          Today's Meetings
                        </Typography>
                        <Badge 
                          badgeContent={todayEvents.today_meetings.length} 
                          color="success"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      <IconButton size="small">
                        {expandedEventId === 'today_meetings' ? 
                          <KeyboardArrowUpIcon fontSize="small" /> : 
                          <KeyboardArrowDownIcon fontSize="small" />
                        }
                      </IconButton>
                    </Box>
                    
                    {expandedEventId === 'today_meetings' && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        {todayEvents.today_meetings.length > 0 ? (
                          todayEvents.today_meetings.map(meeting => (
                            <EventCard key={`meeting-${meeting.id}`} event={meeting} isFollowup={false} />
                          ))
                        ) : (
                          <Alert severity="info">No meetings scheduled for today.</Alert>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={1}
                    sx={{ 
                      mb: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.50' }
                      }}
                      onClick={() => setExpandedEventId(expandedEventId === 'today_followups' ? null : 'today_followups')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventIcon color="info" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium" color="info.main">
                          Today's Follow-ups
                        </Typography>
                        <Badge 
                          badgeContent={todayEvents.today_followups.length} 
                          color="info"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      <IconButton size="small">
                        {expandedEventId === 'today_followups' ? 
                          <KeyboardArrowUpIcon fontSize="small" /> : 
                          <KeyboardArrowDownIcon fontSize="small" />
                        }
                      </IconButton>
                    </Box>
                    
                    {expandedEventId === 'today_followups' && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        {todayEvents.today_followups.length > 0 ? (
                          todayEvents.today_followups.map(followup => (
                            <EventCard key={`followup-${followup.id}`} event={followup} isFollowup={true} />
                          ))
                        ) : (
                          <Alert severity="info">No follow-ups scheduled for today.</Alert>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            ) : viewMode === 'upcoming' ? (
              <UpcomingEventsView 
                meetings={todayEvents.upcoming_meetings} 
                followups={todayEvents.upcoming_followups} 
              />
            ) : (
              <PastEventsView 
                meetings={todayEvents.past_meetings} 
                followups={todayEvents.past_followups} 
              />
            )}
          </>
        )}

        {/* Sales Outreach */}
        {selectedEvent && (
          <SalesOutreachShell
            open={openOutreachDialog}
            onClose={handleCloseOutreachDialog}
            recordId={selectedEvent.record_id}
            companyName={selectedEvent.company_name}
            contactInfo={selectedEvent}
            onOutreachUpdate={refreshStats}
          />
        )}
      </Paper>
      )}
    </>
  );
};

export default EventsDashboard; 